// src/oura/ask/guide.ts
//
// Ask Oura (behind VITE_ENABLE_OURA_ASK), job 1: guide staff on Oura. Three kinds of question:
//   How do I…        2 to 4 numbered steps naming the buttons as the screen
//                    labels them, and a link that opens that screen or drawer.
//   Where is…        a sentence and the link straight to it.
//   What does it mean  the figure or status in the screen's own words, with
//                    this client's numbers where the records have them.
//
// Every label here was read from the screen's source (answers.md in the
// build folder lists the file and line for each). Where Oura has no such
// thing, the answer says so plainly. Ask Oura never presses a button: a
// scan, a share, a reply and a note are all a person's press.

import type { AskAnswer, AskBlock, AskCtx, AskGo, AskTopicGroup } from "./types";
import { getBusiness } from "../data";
import type { ClientData } from "./useAskData";
import { dayMonth } from "../visibility/format";
import { pct } from "../visibility/model";
import { groupRows } from "../delivery/listModel";
import { closest, contactOf, hrefs, n, p, s, src, visSource } from "./text";
import { SCAN_DRAFT_UI_ON, scanDraftMode } from "../newscan/flags";

export interface GuideCtx {
    /** Null on the Businesses page (all clients). */
    businessId: string | null;
    name: string | null;
    d: ClientData | null;
}

type Job = "how" | "where" | "mean";

interface Topic {
    id: string;
    job: Job;
    /** The question as a chip, in this scope. */
    q: (c: GuideCtx) => string;
    /** The words that name the topic. */
    match: RegExp;
    /** Answers without a how / where / what-does cue ("Rescan", "mark it done"). */
    strong?: RegExp;
    answer: (c: GuideCtx, text: string) => AskAnswer;
}

/* ------------------------------------------------------------------------- *
 * Cues
 * ------------------------------------------------------------------------- */

const CUE: Record<Job, RegExp> = {
    how: /\bhow (do|can|should|would) (i|we|you)\b|\bhow to\b|\bhow does\b/,
    where: /\b(where|find|open|show me|take me|go to)\b/,
    mean: /\b(mean|means|meaning|stand for|explain)\b|\bwhat (does|do)\b/,
};

/** Any guide cue at all: a how, where or what-does question that matched no topic gets the menu. */
export const isGuideCue = (t: string): boolean => /\bhow (do|can|should) (i|we)\b|\bwhere (is|are|do|can)\b|\bwhat does\b|\bmean\b/.test(t);

/* ------------------------------------------------------------------------- *
 * Shapes
 * ------------------------------------------------------------------------- */

const steps = (list: string[], go: AskGo): AskBlock => ({ kind: "steps", steps: list, go });
const goTo = (label: string, to: string): AskBlock => ({ kind: "go", go: { label, to } });

/** Inside a client the answer links to its screen; on Businesses, step 1 is opening the client. */
function forClient(c: GuideCtx, list: string[], go: (id: string) => AskGo): AskBlock {
    if (c.businessId) return steps(list, go(c.businessId));
    return steps(["On Businesses, press the client's row to open it.", ...list].slice(0, 4), { label: "Open Businesses", to: hrefs.businesses() });
}

const nameOf = (c: GuideCtx) => c.name ?? "the client";

/* ------------------------------------------------------------------------- *
 * Docs on the board
 * ------------------------------------------------------------------------- */

/** The catalogue's doc types, as Create a doc lists them (clientDocsRules.ts DOC_EXPECTED). */
export const DOC_TYPES = ["Answer Plan", "GBP Pack", "Copy Pack", "Monthly Performance Report", "Monthly Work Snapshot"] as const;
/** Doc types on the board that Create a doc does not make, but staff ask for by name (the Monthly Update, retired by ourteam-backend#6868). */
const OTHER_DOCS = ["Business Details", "Page Titles Review", "Monthly Update"];

export function docTypeIn(text: string): string | null {
    const t = text.toLowerCase();
    return [...DOC_TYPES, ...OTHER_DOCS].find((x) => t.includes(x.toLowerCase())) ?? null;
}

function boardDoc(d: ClientData | null, docType: string) {
    const docs = (d?.board?.documents ?? []).filter((x) => (x.docType ?? "").toLowerCase() === docType.toLowerCase());
    docs.sort((a, b) => (b.modifiedAt ?? "").localeCompare(a.modifiedAt ?? ""));
    return docs[0] ?? null;
}

/** A doc the Share view can share: a client Google Doc not shared yet, else any client Google Doc. */
function shareableDoc(d: ClientData | null) {
    const docs = (d?.board?.documents ?? []).filter((x) => x.fileType === "gdoc" && !x.notClientDoc && x.docType);
    return docs.find((x) => !x.sharing.clientCanSee) ?? docs[0] ?? null;
}

/* ------------------------------------------------------------------------- *
 * Delivery statuses
 * ------------------------------------------------------------------------- */

const STATUS_WORDS: { re: RegExp; lead: string }[] = [
    {
        re: /watching/,
        lead: "Oura has no status called Watching the page. The nearest is Checking: a person did the work, and Oura reads the page again to see it.",
    },
    { re: /needs us/, lead: "Needs us means the next move is ours. The row shows who has it and the date it is due, in red once it is late." },
    {
        re: /waiting on (the )?client|client'?s turn/,
        lead: "Waiting on client means we asked the client for something and the step waits on their answer. The row shows how long, as Client · 8 days. Overview calls the same thing Client's turn.",
    },
    {
        re: /checking|re-?check/,
        lead: "Checking means a person did the work and Oura reads it again: tonight, or straight away when someone presses Check now. The row says Re-check tonight.",
    },
    { re: /came back/, lead: "Came back means a fix that was done before and the latest scan found the problem again. It moves back to Needs us." },
    { re: /\blater\b/, lead: "Later holds next month's steps. They move up when their month starts." },
];

/* ------------------------------------------------------------------------- *
 * The topics
 * ------------------------------------------------------------------------- */

const TOPICS: Topic[] = [
    /* ------------------------------ How do I ------------------------------ */
    {
        id: "how-rescan",
        job: "how",
        q: (c) => (c.businessId ? "How do I start a rescan?" : "How do I start a new scan?"),
        match: /\b(re-?scan|rerun|re-run|scan (it |them )?again|new scan|start (a |the )?scan|run (a |another )?scan|assessment)\b|\b(fresh|new|newer|updated|latest|current|up to date|up-to-date) (numbers|figures|results|data|scores?)\b|\bre-?measure\b|\bmeasure (it |them )?again\b|\brefresh (the )?(numbers|figures|results|data|scan)\b/,
        strong: /\b(re-?scan|rerun|re-run|scan (it |them )?again|run (a |another )?(new )?scan|ai check again)\b|\b(fresh|newer|updated|up to date|up-to-date) (numbers|figures|results|data|scores?)\b|\bre-?measure\b|\bmeasure (it |them )?again\b|\brefresh (the )?(numbers|figures|results|data|scan)\b/,
        answer: (c) => {
            const d = c.d;
            // The same switch Businesses and the client header read (newscan/flags.ts): on, New scan is the
            // full-page flow and a client's header has Edit and rescan; off, the New scan modal and Run AI check again.
            const flowOn = SCAN_DRAFT_UI_ON && scanDraftMode() !== "absent";
            if (!c.businessId) {
                return {
                    blocks: [
                        p(s("A new scan starts from New scan, at the top right of Businesses. Ask Oura does not start one, because a scan is paid.")),
                        steps(
                            flowOn
                                ? [
                                      "Press New scan at the top right of Businesses. On Website, paste the company's site, press Read the site, then Continue.",
                                      "On Business, check the business name, outlets and areas served match the client before you press Draft the 50. Oura fills them from the site and can get them wrong.",
                                      "On Review, answer every card under To decide, then press Approve the 50.",
                                      "On Start, read every line again. Start the scan asks once, This starts a paid scan, so press Start only when the details are right.",
                                  ]
                                : [
                                      "Press New scan at the top right of Businesses.",
                                      "On Step 1 of 3, type the company domain and press Continue. Oura reads the site and fills in the next step.",
                                      "On Step 2 of 3, check the business name, city and anchor areas match the client before you press Continue. Oura fills them from the site and can get them wrong.",
                                      "On Step 3 of 3, read every line again. Start assessment starts a paid scan, so press it only when the details are right.",
                                  ],
                            { label: "Open Businesses", to: hrefs.businesses() },
                        ),
                    ],
                    followUps: ["What needs me today?", "What does Quiet mean?"],
                };
            }
            const id = c.businessId;
            const last = d?.vis?.capturedAt ?? d?.snapshot?.capturedAt ?? null;
            // What the last scan was for, from the client's own record, so staff can check the sheet against it.
            const biz = getBusiness(id);
            const was = biz ? `${biz.name}${biz.city ? ` in ${biz.city}` : ""}${biz.vertical ? `, as a ${biz.vertical}` : ""}` : null;
            const matchWho = biz?.city ? `${nameOf(c)} (${biz.city})` : "the client";
            const lead = [
                s(
                    flowOn
                        ? `Rescans start from Edit and rescan, at the top right of every ${nameOf(c)} page (Continue the draft while a draft of the 50 is open). Ask Oura does not start one, because a scan is paid.`
                        : `Rescans start from Run AI check again, at the top right of ${nameOf(c)}'s Overview, Visibility and Site. Ask Oura does not start one, because a scan is paid.`,
                ),
            ];
            if (last) lead.push(s(`The last scan was on ${dayMonth(last)}${was ? `, for ${was}` : ""}.`, src("History", hrefs.history(id))));
            return {
                blocks: [
                    p(...lead),
                    steps(
                        flowOn
                            ? [
                                  "Press Edit and rescan at the top right of the page. The 50 open on Review.",
                                  `Press Business in the steps at the top. Oura filled it from the site, and it can be wrong. Check the business name, outlets and areas served match ${matchWho}, fix any that do not (Update the 50 drafts them again), then go back to Review.`,
                                  "On Review, answer every card under To decide, then press Approve the 50.",
                                  "On Start, pick When: At the next monthly check is free, Rescan now is paid. Start the scan asks once, This starts a paid scan, so press Start only when the business, city and areas are right.",
                              ]
                            : [
                                  "Press Run AI check again at the top right of Overview, Visibility or Site.",
                                  `The New scan sheet opens on Step 2 of 3, filled in from what Oura read on the site, and it can be wrong. Check the business name, city and areas match ${matchWho}, fix any that do not, then press Continue.`,
                                  "On Step 3 of 3, read every line again. Start assessment starts a paid scan, so press it only when the business, city and areas are right. The page updates when the scan finishes.",
                              ],
                        { label: "Open Overview", to: hrefs.overview(id) },
                    ),
                ],
                followUps: ["What changed since the last scan?", "How do I read a scan?"],
            };
        },
    },
    {
        id: "how-share",
        job: "how",
        q: () => "How do I share a doc with the client?",
        match: /\bshare\b(?!.*\breport\b)/,
        answer: (c) => {
            const doc = c.businessId ? shareableDoc(c.d) : null;
            const lead = [s("A doc is shared from its drawer on the Client tab. It is shared comment-only, and the client hears in their bell in Oura.")];
            if (doc && c.businessId && !doc.sharing.clientCanSee) lead.push(s(`The ${doc.docType} is not shared yet.`, src(doc.docType ?? "Document", hrefs.doc(c.businessId, doc.id))));
            return {
                blocks: [
                    p(...lead),
                    forClient(
                        c,
                        [
                            "On the Client tab, press the doc's row on the Documents card to open it.",
                            "Under Questions and sharing, press Share with the client.",
                            "Fix any check marked in red, pick the client's address, then press Share. The drawer then offers Write the review email.",
                        ],
                        (id) => (doc ? { label: `Open the ${doc.docType}`, to: hrefs.doc(id, doc.id) } : { label: "Open the Client tab", to: hrefs.client(id) }),
                    ),
                ],
                followUps: ["How do I create a document?", "What's waiting on the client?"],
            };
        },
    },
    {
        id: "how-reply",
        job: "how",
        q: () => "How do I reply to a client request?",
        match: /\b(reply|respond|answer|write back)\b/,
        strong: /\b(draft|write) (a |the )?(reply|response)\b/,
        answer: (c, text) => {
            const id = c.businessId;
            const open = (c.d?.requests ?? []).filter((r) => r.status === "open").sort((a, b) => b.at.localeCompare(a.at));
            const r = open[0];
            const lead = [];
            if (/\b(draft|write) (a |the )?(reply|response)\b/.test(text)) lead.push(s("Ask Oura does not write replies to clients. A person writes and sends them from the request."));
            if (id && r) {
                const note = r.note.length > 70 ? `${r.note.slice(0, 68).trimEnd()}…` : r.note;
                lead.push(s(`${r.requestedBy} has ${n(open.length, "open request")}. The newest, from ${dayMonth(r.at)}, says “${note}”`, src("Client tab · request", hrefs.request(id, r.id))));
            } else if (id) {
                lead.push(s(`${contactOf(c.d ?? { agreed: null, requests: null })} has no open request right now.`, src("Client tab", hrefs.client(id))));
            }
            const who = r?.requestedBy ?? "the client";
            const blocks: AskBlock[] = [];
            if (lead.length) blocks.push(p(...lead));
            blocks.push(
                forClient(
                    c,
                    [
                        "On the Client tab, press the request on the Requests card to open it.",
                        `Write in the Reply to ${who} box. The line under it shows the address it emails and who signs it.`,
                        "Press Send reply. To close it without a reply, press Done, and Oura asks first.",
                    ],
                    (bid) => (r ? { label: "Open the request", to: hrefs.request(bid, r.id) } : { label: "Open the Client tab", to: hrefs.client(bid) }),
                ),
            );
            return { blocks, followUps: ["What's waiting on us?", "How do I send this week's note?"] };
        },
    },
    {
        id: "how-read-scan",
        job: "how",
        q: () => "How do I read a scan?",
        match: /\b(read|understand|look at|check|see)\b.*\b(scan|results?|visibility)\b|\bscan results?\b/,
        answer: (c) => {
            const v = c.d?.vis;
            const lead = v && c.d
                ? [s(`The ${dayMonth(v.capturedAt)} scan is on Visibility. AI names ${nameOf(c)} in ${v.named} of ${v.of} questions.`, visSource(c.d))]
                : [s("Every scan is read on Visibility.")];
            return {
                blocks: [
                    p(...lead),
                    forClient(
                        c,
                        [
                            "Open Visibility. The first tile, AI assistants name it, is the headline count, and the line under it says how a question is counted.",
                            "By engine shows each engine's share. Press a ring to see that engine only.",
                            "Named over time is the trend across scans. Question history, at the top, shows when the questions changed.",
                            "In the Questions table, press a row to read the answers and who is named instead. Missing you lists the questions no engine names them for.",
                        ],
                        (id) => ({ label: "Open Visibility", to: hrefs.visibility(id) }),
                    ),
                ],
                followUps: ["What do the engine rings mean?", "Who is named instead?"],
            };
        },
    },
    {
        id: "how-create-doc",
        job: "how",
        q: () => "How do I create a document?",
        match: /\b(create|make|start|new|write|draft)\b.*\b(doc|docs|document|answer plan|monthly update|monthly performance report|monthly work snapshot|copy pack|gbp pack)\b/,
        answer: (c, text) => {
            const type = docTypeIn(text);
            const picked = type && (DOC_TYPES as readonly string[]).includes(type) ? type : null;
            return {
                blocks: [
                    p(s("Documents are made in Create a doc, on the Client tab's Documents card. A new doc is never shared on create.")),
                    forClient(
                        c,
                        [
                            "On the Client tab, press Create a doc on the Documents card.",
                            picked
                                ? `The ${picked} is already picked. Choose how it is made: From the template, From a Google Doc we have or From a Word file in the folder. Check its codes and folder.`
                                : "Pick the doc type. Then choose how it is made: From the template, From a Google Doc we have or From a Word file in the folder, and check its codes and folder.",
                            "The file name and the title lines fill in as you pick. Press Create the doc.",
                            "Read it, then share it from its drawer.",
                        ],
                        (id) => ({ label: picked ? `Create the ${picked}` : "Open Create a doc", to: hrefs.create(id, picked) }),
                    ),
                ],
                followUps: ["How do I share a doc with the client?", "Start the monthly performance report"],
            };
        },
    },
    {
        id: "how-note",
        job: "how",
        q: () => "How do I send this week's note?",
        match: /\b(week'?s note|weekly note|friday note|note to the client|this week.s note)\b/,
        answer: (c) => ({
            blocks: [
                p(s(`This week's note is a short line to ${nameOf(c)}. It goes to the top of their Home, to their bell, and by email.`)),
                forClient(
                    c,
                    [
                        "On the Client tab, find This week's note.",
                        "Write the note, or press Draft from this week to start from what Delivery shows for the week.",
                        "Press Send, then Send it to confirm. It cannot be unsent.",
                    ],
                    (id) => ({ label: "Open this week's note", to: hrefs.note(id) }),
                ),
            ],
            followUps: ["Catch me up", "How do I reply to a client request?"],
        }),
    },
    {
        id: "how-recheck",
        job: "how",
        q: () => "How do I get Oura to re-check a fix?",
        match: /\b(re-?check|check now)\b|\bmark\b.*\b(done|complete|completed|fixed)\b|\btick\b.*\boff\b|\bclose (the |a |this |that )?(task|step)\b/,
        strong: /\bmark\b.*\b(done|complete|completed)\b|\btick\b.*\boff\b|\bclose (the |a |this |that )?(task|step)\b/,
        answer: (c) => {
            const id = c.businessId;
            const checking = (c.d?.delivery?.deliverables ?? []).filter((x) => x.group === "checking").length;
            const lead = [s("Ask Oura does not mark anything done. A step closes itself when Oura sees the proof: the fix on the page, the page live, the client's answer.")];
            if (id && c.d?.delivery) lead.push(s(`${checking ? `${n(checking, "step")} ${checking === 1 ? "waits" : "wait"} on Oura's re-check now.` : "Nothing waits on Oura's re-check now."}`, src("Delivery", hrefs.delivery(id))));
            return {
                blocks: [
                    p(...lead),
                    forClient(
                        c,
                        [
                            "Open Delivery.",
                            checking
                                ? `Press Check now · ${checking} waiting, to the right of Next steps, Checklist, By month and Plan. Oura reads them now instead of tonight.`
                                : "When steps wait on Oura, Check now shows to the right of Next steps, Checklist, By month and Plan, with the number waiting. Press it to read them now instead of tonight.",
                            "Steps Oura confirms move to Done. A fix the next scan finds again shows Came back.",
                        ],
                        (bid) => ({ label: "Open Delivery", to: hrefs.delivery(bid) }),
                    ),
                ],
                followUps: ["What do the Delivery statuses mean?", "What's waiting on us?"],
            };
        },
    },
    {
        id: "how-fix-issue",
        job: "how",
        q: () => "How do I fix a site issue?",
        match: /\b(fix|solve|sort out)\b.*\b(issues?|errors?|warnings?|problems?|audit)\b|\bhow to fix\b/,
        answer: (c) => {
            const top = c.d?.issues?.top ?? null;
            const id = c.businessId;
            const lead = top && id ? [s(`The worst issue on the last crawl is ${top.label}, on ${n(top.pages, "page")}.`, src("Site", hrefs.site(id)))] : [s("Site issues are fixed from the Site screen.")];
            return {
                blocks: [
                    p(...lead),
                    forClient(
                        c,
                        [
                            "Open Site and scroll to What the crawl found.",
                            "Press How to fix on the issue's row.",
                            "The drawer shows About the issue, How to fix and Affected pages.",
                        ],
                        (bid) => (top ? { label: `Open How to fix: ${top.label}`, to: hrefs.issue(bid, top.key) } : { label: "Open Site", to: hrefs.site(bid) }),
                    ),
                ],
                followUps: ["What do Error, Warning and Notice mean?", "Where is the Page workshop?"],
            };
        },
    },

    /* ------------------------------ Where is ------------------------------ */
    {
        id: "where-doc",
        job: "where",
        q: () => "Where is the Answer Plan?",
        match: /\b(answer plan|copy pack|gbp pack|monthly update|monthly performance report|monthly work snapshot|business details|page titles review)\b/,
        answer: (c, text) => {
            const type = docTypeIn(text) ?? "Answer Plan";
            const id = c.businessId;
            if (!id) {
                return {
                    blocks: [p(s(`Each client's ${type} is on their Client tab, on the Documents card. Documents (all clients), on Businesses, lists them across clients.`)), goTo("Open Documents (all clients)", hrefs.allDocs())],
                    followUps: ["What needs me today?"],
                };
            }
            const doc = boardDoc(c.d, type);
            if (!doc) {
                const canMake = (DOC_TYPES as readonly string[]).includes(type);
                return {
                    blocks: [
                        p(s(`There is no ${type} for ${nameOf(c)} in the client's folder yet.`, src("Client tab · Documents", hrefs.client(id)))),
                        canMake ? { kind: "start", docType: type, text: startText(type), to: hrefs.create(id, type) } : goTo("Open the Client tab", hrefs.client(id)),
                    ],
                    followUps: ["How do I create a document?", "What's waiting on the client?"],
                };
            }
            const shared = doc.sharing.clientCanSee ? `Shared${doc.sharing.sharedAt ? ` ${dayMonth(doc.sharing.sharedAt)}` : ""} with the client.` : "Not shared with the client yet.";
            return {
                blocks: [p(s(`The ${type} is on the Client tab, on the Documents card. ${shared}`, src(type, hrefs.doc(id, doc.id)))), goTo(`Open the ${type}`, hrefs.doc(id, doc.id))],
                followUps: ["How do I share a doc with the client?", "What's waiting on the client?"],
            };
        },
    },
    {
        id: "where-contract",
        job: "where",
        q: () => "Where is the contract?",
        match: /\b(contract|timeline|key pages|schedule)\b/,
        answer: (c, text) => {
            const timeline = /\b(timeline|schedule)\b/.test(text);
            const lead = timeline
                ? s("The timeline is in Delivery, under Plan, below the plan: Publish, the dates and the schedule. Download timeline sits at its top right.")
                : s("The contract is in Delivery, under Plan: the What was sold card, below the key pages, with The plan under it.");
            return {
                blocks: [
                    p(lead),
                    c.businessId
                        ? goTo("Open Plan", hrefs.plan(c.businessId))
                        : forClient(c, ["Press Delivery in the sidebar, then Plan."], (id) => ({ label: "Open Delivery", to: hrefs.delivery(id) })),
                ],
                followUps: ["What's waiting on us?", "What should I do next?"],
            };
        },
    },
    {
        id: "where-scans",
        job: "where",
        q: () => "Where are the past scans?",
        match: /\b((past|previous|old|older|earlier|all|last) scans?|scan history|history)\b/,
        answer: (c) => ({
            blocks: [
                p(s("Every scan is on History, under Scans. Activity, beside it, is the record of what we delivered.")),
                c.businessId ? goTo("Open History", hrefs.history(c.businessId)) : forClient(c, ["Press History in the sidebar."], (id) => ({ label: "Open History", to: hrefs.history(id) })),
            ],
            followUps: ["What changed since the last scan?", "How do I read a scan?"],
        }),
    },
    {
        id: "where-workshop",
        job: "where",
        q: () => "Where is the Page workshop?",
        match: /\b(page workshop|workshop)\b/,
        answer: (c) => ({
            blocks: [
                p(s("The Page workshop is under Site: press Site in the sidebar, then Page workshop. Pick a page to see Oura's proposed changes beside it.")),
                c.businessId ? goTo("Open the Page workshop", hrefs.pages(c.businessId)) : forClient(c, ["Press Site in the sidebar, then Page workshop."], (id) => ({ label: "Open the Page workshop", to: hrefs.pages(id) })),
            ],
            followUps: ["Start the page drafts", "How do I fix a site issue?"],
        }),
    },
    {
        id: "where-requests",
        job: "where",
        q: () => "Where are the client's requests?",
        match: /\brequests?\b/,
        answer: (c) => {
            const id = c.businessId;
            const open = (c.d?.requests ?? []).filter((r) => r.status === "open");
            return {
                blocks: [
                    p(
                        id
                            ? s(`The client's requests are on the Client tab, on the Requests card. ${open.length ? `${open.length} ${open.length === 1 ? "is" : "are"} open.` : "None is open."}`, src("Client tab", hrefs.client(id)))
                            : s("Each client's requests are on their Client tab, on the Requests card."),
                    ),
                    id ? goTo("Open the Client tab", hrefs.client(id)) : forClient(c, ["Press Client in the sidebar."], (bid) => ({ label: "Open the Client tab", to: hrefs.client(bid) })),
                ],
                followUps: ["How do I reply to a client request?", "What's waiting on us?"],
            };
        },
    },
    {
        id: "where-today",
        job: "where",
        q: () => "Where do I see what is due today?",
        match: /\b(due today|today list|today view|today tab|today's list|due across)\b|\btoday\b/,
        answer: () => ({
            blocks: [p(s("Every step due across clients is on Businesses, under Today. Late ones and fixes that came back come first.")), goTo("Open Today", hrefs.today())],
            followUps: ["What needs me today?", "What's late?"],
        }),
    },
    {
        id: "where-all-docs",
        job: "where",
        q: () => "Where are all the documents?",
        match: /\b(all (the )?(documents|docs)|documents across|docs across|every doc)\b/,
        answer: () => ({
            blocks: [p(s("Every client's documents are on Businesses, under Documents (all clients).")), goTo("Open Documents (all clients)", hrefs.allDocs())],
            followUps: ["Who has gone quiet?", "What needs me today?"],
        }),
    },
    {
        id: "where-competitors",
        job: "where",
        q: () => "Where do I see who is named instead?",
        match: /\b(competitors?|rivals?|named instead)\b/,
        answer: (c) => ({
            blocks: [
                p(s("Who is named instead is on Visibility, under Competitors. Each row in the Questions table also lists who is named instead on that question.")),
                c.businessId ? goTo("Open Competitors", hrefs.competitors(c.businessId)) : forClient(c, ["Press Visibility in the sidebar, then Competitors."], (id) => ({ label: "Open Competitors", to: hrefs.competitors(id) })),
            ],
            followUps: ["Who is named instead?", "How do I read a scan?"],
        }),
    },

    /* --------------------------- What does it mean ------------------------ */
    {
        id: "mean-named",
        job: "mean",
        q: (c) => (c.d?.vis ? `What does ${c.d.vis.named} of ${c.d.vis.of} mean?` : "What does 23 of 50 mean?"),
        match: /\b\d+ of \d+\b|\bnamed in\b|\bheadline\b|\bai assistants name it\b/,
        answer: (c) => {
            const v = c.d?.vis;
            if (!v || !c.d || !c.businessId) {
                return {
                    blocks: [
                        p(s("On Visibility and Overview, a number like 23 of 50 is how many of the client's questions an AI engine names them for. The line under the tile says which engines count.")),
                        forClient(c, ["Press Visibility in the sidebar."], (id) => ({ label: "Open Visibility", to: hrefs.visibility(id) })),
                    ],
                    followUps: ["What do the engine rings mean?"],
                };
            }
            const id = c.businessId;
            const sentences = [s(`${v.named} of ${v.of} means AI names ${nameOf(c)} in ${v.named} of the ${v.of} questions on the ${dayMonth(v.capturedAt)} scan.`, visSource(c.d)), s(v.rule)];
            if (v.verdict) sentences.push(s(`Visibility calls that ${v.verdict.toLowerCase()} visibility, and Overview opens with the same count, Named in ${v.named} of ${v.of}.`, src("Overview", hrefs.overview(id))));
            return { blocks: [p(...sentences), goTo("Open Visibility", hrefs.visibility(id))], followUps: ["What do the engine rings mean?", "How do I read a scan?"] };
        },
    },
    {
        id: "mean-rings",
        job: "mean",
        q: () => "What do the engine rings mean?",
        match: /\b(rings?|by engine|per engine|percent|percentage)\b|%/,
        answer: (c) => {
            const v = c.d?.vis;
            const blocks: AskBlock[] = [
                p(
                    s(`Each ring under By engine is one engine: the share of the questions it was measured on that name ${nameOf(c)}.`),
                    s("An engine can have fewer questions than the set when some were not measured on it. Press a ring to see that engine only."),
                ),
            ];
            if (v && c.d) {
                const cite = visSource(c.d);
                // An engine nothing was measured on has no share: say so, never 0%.
                blocks.push({
                    kind: "table",
                    rows: v.engines.map((e) => {
                        const share = pct(e.named, e.of);
                        return { label: e.label, value: share === null ? "Not measured yet" : `${share}%, ${e.named} of ${e.of}`, cite };
                    }),
                });
            }
            blocks.push(c.businessId ? goTo("Open Visibility", hrefs.visibility(c.businessId)) : forClient(c, ["Press Visibility in the sidebar."], (id) => ({ label: "Open Visibility", to: hrefs.visibility(id) })));
            return { blocks, followUps: [c.d?.vis ? `What does ${c.d.vis.named} of ${c.d.vis.of} mean?` : "What does 23 of 50 mean?", "How is this client doing on AI?"] };
        },
    },
    {
        id: "mean-site-column",
        job: "mean",
        q: () => "What do Answers it, Thin and No page mean?",
        match: /\b(answers it|thin|no page|your site)\b/,
        answer: (c) => ({
            blocks: [
                p(
                    s(`The Your site column on Visibility says whether ${nameOf(c)}'s own site answers the question: Answers it, Thin when the closest page says too little, or No page.`),
                    s("The filter Your site doesn't answer shows the Thin and No page rows."),
                ),
                c.businessId ? goTo("Open Visibility", hrefs.visibility(c.businessId)) : forClient(c, ["Press Visibility in the sidebar."], (id) => ({ label: "Open Visibility", to: hrefs.visibility(id) })),
            ],
            followUps: ["How do I read a scan?", "Where is the Page workshop?"],
        }),
    },
    {
        id: "mean-delivery",
        job: "mean",
        q: () => "What do the Delivery statuses mean?",
        match: /\b(needs us|waiting on (the )?client|client'?s turn|checking|re-?check tonight|came back|later|watching( the page)?|statuse?s?|squares?)\b/,
        answer: (c, text) => {
            const id = c.businessId;
            const hit = STATUS_WORDS.find((w) => w.re.test(text));
            const sentences = [s(hit ? hit.lead : "Next steps on Delivery puts every step in one group: Needs us, Waiting on client, Checking, Done or Later.")];
            const blocks: AskBlock[] = [p(...sentences)];
            const pl = c.d?.delivery;
            if (id && pl) {
                // The groups exactly as Next steps lists them (listModel.ts), so every count matches the screen.
                const cite = src("Delivery", hrefs.delivery(id));
                const groups = groupRows(pl.deliverables, pl.month);
                const count = (g: string) => groups.find((x) => x.key === g)?.rows.length ?? 0;
                blocks.push({
                    kind: "table",
                    rows: [
                        { label: "Needs us", value: `${count("needs_us")}, the next move is ours`, cite },
                        { label: "Waiting on client", value: `${count("waiting_client")}, waits on their answer`, cite },
                        { label: "Checking", value: `${count("checking")}, Oura reads the work again`, cite },
                        { label: "Done", value: `${count("done")} this month, Oura saw the proof`, cite },
                        { label: "Later", value: `${count("later")}, next month's steps`, cite },
                    ],
                });
            }
            blocks.push(id ? goTo("Open Delivery", hrefs.delivery(id)) : forClient(c, ["Press Delivery in the sidebar."], (bid) => ({ label: "Open Delivery", to: hrefs.delivery(bid) })));
            return { blocks, followUps: ["What's waiting on us?", "How do I get Oura to re-check a fix?"] };
        },
    },
    {
        id: "mean-severity",
        job: "mean",
        q: () => "What do Error, Warning and Notice mean?",
        match: /\b(errors?|warnings?|notices?|severity|severities)\b/,
        answer: (c) => {
            const top = c.d?.issues?.top ?? null;
            const sentences = [
                s("On Site, every crawl issue is an Error, a Warning or a Notice. Error, in rose, is the most serious, then Warning in amber, then Notice in grey."),
                s("The severity is the crawl's own, and the list puts the most serious first."),
            ];
            if (top && c.businessId) sentences.push(s(`The first one now is ${top.label}, on ${n(top.pages, "page")}.`, src("Site", hrefs.site(c.businessId))));
            return {
                blocks: [p(...sentences), c.businessId ? goTo("Open Site", hrefs.site(c.businessId)) : forClient(c, ["Press Site in the sidebar."], (id) => ({ label: "Open Site", to: hrefs.site(id) }))],
                followUps: ["How do I fix a site issue?"],
            };
        },
    },
    {
        id: "mean-rating",
        job: "mean",
        q: () => "What does Overall rating, capped mean?",
        match: /\b(overall rating|rating|capped|cap|\d of 5|out of 5)\b/,
        answer: (c) => ({
            blocks: [
                p(
                    s("Overall rating, shown when you hover or tap the line under Overview's title, is the backend's level from 1 to 5 over the area scores. It is not a rating of the AI count before it."),
                    s("Capped means something holds it down, and that line says what: Site health below 50, or AI naming the business through directories rather than its own site."),
                ),
                c.businessId ? goTo("Open Overview", hrefs.overview(c.businessId)) : forClient(c, ["Overview opens first."], (id) => ({ label: "Open Overview", to: hrefs.overview(id) })),
            ],
            followUps: ["Catch me up", "How do I read a scan?"],
        }),
    },
    {
        id: "mean-quiet",
        job: "mean",
        q: () => "What does Quiet mean?",
        match: /\bquiet\b/,
        answer: () => ({
            blocks: [
                p(
                    s("Quiet, a column on Businesses, is the time since anything happened for a client: the later of the last scan and the last thing we delivered. A client nothing has been done for counts as the quietest."),
                    s("Who has gone quiet, asked here, is a different list: clients who have not answered any document we sent."),
                ),
                goTo("Open Businesses", hrefs.businesses()),
            ],
            followUps: ["Who has gone quiet?", "What needs me today?"],
        }),
    },
];

/** The doc start card's line: what pressing it does, and what it does not. */
export function startText(docType: string): string {
    return `Opens Create a doc on the Client tab with the ${docType} picked. Nothing is made until you press Create the doc, and a new doc is never shared on create.`;
}

/* ------------------------------------------------------------------------- *
 * Matching and the menu
 * ------------------------------------------------------------------------- */

/** The guide answer for a question, or null when it is not a guide question. */
export function guideAnswer(text: string, c: GuideCtx, ctx?: AskCtx): AskAnswer | null {
    const t = text.toLowerCase().replace(/[’]/g, "'");
    if (/^\s*how do i\W*$/.test(t)) return ctx?.menuShown ? guideMiss(c, t, "The how-to list is earlier in this chat.") : menuAnswer(c);
    for (const topic of TOPICS) {
        if (topic.strong?.test(t)) return topic.answer(c, t);
    }
    // A cue picks the job first, so "where is the Answer Plan" opens it and
    // "how do I create the Answer Plan" walks through Create a doc.
    const jobs: Job[] = (["how", "where", "mean"] as Job[]).filter((j) => CUE[j].test(t));
    for (const j of jobs) {
        const topic = TOPICS.find((x) => x.job === j && x.match.test(t));
        if (topic) return topic.answer(c, t);
    }
    return null;
}

/** The questions the How do I menu offers, grouped, in this scope. */
export function guideGroups(c: GuideCtx): AskTopicGroup[] {
    const q = (id: string) => TOPICS.find((x) => x.id === id)!.q(c);
    if (!c.businessId) {
        return [
            { title: "How do I", items: [q("how-rescan"), q("how-share"), q("how-reply"), q("how-create-doc")] },
            { title: "Where is", items: [q("where-today"), q("where-all-docs")] },
            { title: "What does it mean", items: [q("mean-quiet"), q("mean-delivery")] },
        ];
    }
    return [
        {
            title: "How do I",
            items: [q("how-rescan"), q("how-share"), q("how-reply"), q("how-read-scan"), q("how-create-doc"), q("how-note"), q("how-recheck"), q("how-fix-issue")],
        },
        { title: "Where is", items: [q("where-doc"), q("where-contract"), q("where-scans"), q("where-workshop"), q("where-competitors")] },
        { title: "What does it mean", items: [q("mean-named"), q("mean-rings"), q("mean-delivery"), q("mean-site-column"), q("mean-severity")] },
    ];
}

function menuAnswer(c: GuideCtx): AskAnswer {
    return {
        blocks: [
            p(s(`Pick a question, or type your own. Each answer names the buttons as ${c.businessId ? `${nameOf(c)}'s screens` : "the screens"} show them and links to the screen.`)),
            { kind: "topics", groups: guideGroups(c) },
        ],
        followUps: [],
    };
}

/** For a how / where / what-does question no topic covers: say so plainly, then the two closest how-tos. */
export function guideMiss(c: GuideCtx, text = "", lead = "I do not know a screen or button for that in Oura."): AskAnswer {
    return {
        blocks: [p(s(`${lead} Did you mean one of these?`)), { kind: "choices", items: closest(text, guideGroups(c).flatMap((g) => g.items)) }],
        followUps: [],
    };
}

